import BlockRounded from '@mui/icons-material/BlockRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import ManageAccountsRounded from '@mui/icons-material/ManageAccountsRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import PersonAddAlt1Rounded from '@mui/icons-material/PersonAddAlt1Rounded';
import PhonelinkEraseRounded from '@mui/icons-material/PhonelinkEraseRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import type { PlatformAdmin, PlatformRole } from '../../api/m19';
import { useAuth } from '../../auth/AuthProvider';
import { ROLE_LABELS } from '../../auth/capabilities';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DataTable, SearchField, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { StatGrid, StatTile } from '../../components/ui';
import { count, dateTime, initials, relativeTime } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { CreateAdminDialog, EditAdminDialog } from './AdminDialogs';
import { adminErrorMessage } from './messages';

const ROLE_TONE: Record<PlatformRole, 'primary' | 'info' | 'default'> = {
  SUPER_ADMIN: 'primary',
  BILLING: 'info',
  SUPPORT: 'default',
};

export function RoleChip({ role }: { role: PlatformRole }) {
  const tone = ROLE_TONE[role];
  return (
    <Chip
      size="small"
      icon={role === 'SUPER_ADMIN' ? <ShieldRounded /> : undefined}
      label={ROLE_LABELS[role]}
      variant={tone === 'default' ? 'outlined' : 'filled'}
      sx={
        tone === 'default'
          ? undefined
          : (t) => {
              const p = (t.vars ?? t).palette[tone];
              return {
                bgcolor: `color-mix(in srgb, ${p.main} 12%, transparent)`,
                color: p.dark,
                '& .MuiChip-icon': { color: 'inherit' },
                ...t.applyStyles('dark', { color: p.main }),
              };
            }
      }
    />
  );
}

type Pending =
  { kind: 'status'; admin: PlatformAdmin } | { kind: 'mfa'; admin: PlatformAdmin } | null;

export function AdminsPage() {
  const { me } = useAuth();
  const notify = useNotify();
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PlatformAdmin | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [menu, setMenu] = useState<{ el: HTMLElement; admin: PlatformAdmin } | null>(null);

  const query = useQuery({
    queryKey: ['platform-admins'],
    queryFn: () => unwrap(api.GET('/platform/admins')),
  });
  const all = query.data?.data ?? [];
  const term = search.trim().toLowerCase();
  const rows = query.data
    ? all.filter(
        (a) => !term || a.name.toLowerCase().includes(term) || a.email.toLowerCase().includes(term),
      )
    : undefined;
  const active = all.filter((a) => a.status === 'ACTIVE');
  const withMfa = active.filter((a) => a.mfaEnabled).length;

  const refresh = () => qc.invalidateQueries({ queryKey: ['platform-admins'] });

  const runConfirm = async () => {
    if (!pending) return;
    const { admin } = pending;
    try {
      if (pending.kind === 'status') {
        const next = admin.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE';
        await unwrap(
          api.PATCH('/platform/admins/{id}', {
            params: { path: { id: admin.id } },
            body: { status: next, version: admin.version },
          }),
        );
        notify.success(
          next === 'DISABLED'
            ? `${admin.name} is deactivated and signed out everywhere`
            : `${admin.name} can sign in again`,
        );
      } else {
        await unwrap(
          api.POST('/platform/admins/{id}/reset-mfa', { params: { path: { id: admin.id } } }),
        );
        notify.success(`Two-step verification reset for ${admin.name}`);
      }
    } catch (e) {
      enqueueSnackbar(adminErrorMessage(e), { variant: 'error' });
    } finally {
      await refresh();
    }
  };

  const columns: Column<PlatformAdmin>[] = [
    {
      key: 'name',
      header: 'Admin',
      render: (a) => (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              fontSize: 13,
              fontWeight: 700,
              bgcolor: a.status === 'ACTIVE' ? 'primary.main' : 'action.disabledBackground',
              color: a.status === 'ACTIVE' ? 'primary.contrastText' : 'text.secondary',
            }}
          >
            {initials(a.name)}
          </Avatar>
          <Box sx={{ minWidth: 0, maxWidth: { xs: 200, sm: 320 } }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {a.name}
              {a.id === me?.id && (
                <Typography component="span" variant="caption" color="text.secondary">
                  {' '}
                  (you)
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {a.email}
            </Typography>
            <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}>
              <RoleChip role={a.role} />
            </Box>
          </Box>
        </Stack>
      ),
    },
    { key: 'role', header: 'Role', hideBelow: 'sm', render: (a) => <RoleChip role={a.role} /> },
    {
      key: 'status',
      header: 'Status',
      hideBelow: 'sm',
      render: (a) => (
        <StatusChip status={a.status} label={a.status === 'ACTIVE' ? 'Active' : 'Deactivated'} />
      ),
    },
    {
      key: 'mfa',
      header: 'Two-step',
      hideBelow: 'md',
      render: (a) =>
        a.mfaEnabled ? (
          <StatusChip status="SUCCESS" label="MFA on" />
        ) : (
          <StatusChip status="DENIED" label="MFA off" />
        ),
    },
    {
      key: 'lastLogin',
      header: 'Last sign-in',
      hideBelow: 'md',
      render: (a) =>
        a.lastLoginAt ? (
          <Tooltip title={dateTime(a.lastLoginAt)}>
            <Typography variant="body2">{relativeTime(a.lastLoginAt)}</Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Never
          </Typography>
        ),
    },
    {
      key: 'actions',
      header: (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          Actions
        </Box>
      ),
      align: 'right',
      width: 56,
      render: (a) => (
        <IconButton
          aria-label={`Actions for ${a.name}`}
          onClick={(e) => setMenu({ el: e.currentTarget, admin: a })}
        >
          <MoreVertRounded />
        </IconButton>
      ),
    },
  ];

  const targetName = pending?.admin.name ?? '';
  const target = pending?.admin;
  const disabling = pending?.kind === 'status' && target?.status === 'ACTIVE';

  return (
    <Box>
      <PageHeader
        eyebrow="Platform"
        title="Admins"
        subtitle="Platform staff who can sign in to this console. Only super admins manage accounts."
        actions={
          <Button
            variant="contained"
            startIcon={<PersonAddAlt1Rounded />}
            onClick={() => setCreating(true)}
          >
            Add admin
          </Button>
        }
      />

      {query.isError ? (
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      ) : (
        <>
          <StatGrid>
            <StatTile
              label="Active admins"
              value={count(active.length)}
              hint={`${count(all.length - active.length)} deactivated`}
              icon={<ManageAccountsRounded />}
              loading={query.isPending}
            />
            <StatTile
              label="Super admins"
              value={count(active.filter((a) => a.role === 'SUPER_ADMIN').length)}
              tone="info"
              icon={<ShieldRounded />}
              loading={query.isPending}
            />
            <StatTile
              label="Two-step on"
              value={`${count(withMfa)} / ${count(active.length)}`}
              hint={withMfa === active.length ? 'Every active admin' : 'Active admins with MFA'}
              tone={withMfa === active.length ? 'success' : 'warning'}
              loading={query.isPending}
            />
          </StatGrid>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(a) => a.id}
            loading={query.isPending}
            toolbar={
              <SearchField value={search} onChange={setSearch} placeholder="Search name or email" />
            }
            empty={
              <EmptyState
                dense
                icon={<ManageAccountsRounded />}
                title={all.length ? 'No admins match' : 'No admins yet'}
                body={all.length ? 'Try another name or email.' : 'Add the first platform admin.'}
              />
            }
          />
        </>
      )}

      <Menu
        anchorEl={menu?.el}
        open={menu !== null}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menu && [
          <MenuItem
            key="edit"
            onClick={() => {
              setEditing(menu.admin);
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <EditRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit name or role</ListItemText>
          </MenuItem>,
          <MenuItem
            key="status"
            disabled={menu.admin.id === me?.id && menu.admin.status === 'ACTIVE'}
            onClick={() => {
              setPending({ kind: 'status', admin: menu.admin });
              setMenu(null);
            }}
          >
            <ListItemIcon>
              {menu.admin.status === 'ACTIVE' ? (
                <BlockRounded fontSize="small" />
              ) : (
                <CheckCircleOutlineRounded fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText
              secondary={
                menu.admin.id === me?.id && menu.admin.status === 'ACTIVE'
                  ? 'You can’t deactivate yourself'
                  : undefined
              }
            >
              {menu.admin.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
            </ListItemText>
          </MenuItem>,
          <MenuItem
            key="mfa"
            disabled={!menu.admin.mfaEnabled}
            onClick={() => {
              setPending({ kind: 'mfa', admin: menu.admin });
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <PhonelinkEraseRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText secondary={menu.admin.mfaEnabled ? undefined : 'MFA is not set up'}>
              Reset two-step verification
            </ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <CreateAdminDialog open={creating} onClose={() => setCreating(false)} />
      <EditAdminDialog
        admin={editing}
        isSelf={editing?.id === me?.id}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={pending !== null}
        danger={pending?.kind === 'mfa' || disabling}
        title={
          pending?.kind === 'mfa'
            ? `Reset two-step verification for ${targetName}?`
            : disabling
              ? `Deactivate ${targetName}?`
              : `Activate ${targetName}?`
        }
        body={
          pending?.kind === 'mfa'
            ? 'Their authenticator and recovery codes stop working. They set up two-step verification again at their next sign-in. Use this when they have lost their phone.'
            : disabling
              ? 'They are signed out everywhere and can’t sign in until an admin activates them again.'
              : 'They can sign in again with their existing password.'
        }
        confirmLabel={pending?.kind === 'mfa' ? 'Reset MFA' : disabling ? 'Deactivate' : 'Activate'}
        onConfirm={runConfirm}
        onClose={() => setPending(null)}
      />
    </Box>
  );
}
